import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { authForgotPassword, AiSyncMigrationRequiredError } from '@api/auth';
import AuthLayout from '@components/1-atoms/Layouts/AuthLayout/AuthLayout';
import {
  Title,
  Subtitle,
  Form,
  Input,
  SubmitButton,
  ErrorText,
  ErrorBox,
  SuccessBox,
  LinkText,
  InheritLink,
} from '../auth.styled';
import { ActionArea, BackLinkArea } from './ForgotPassword.styled';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [camLoginUrl, setCamLoginUrl] = useState('');

  const schema = Yup.object({ email: Yup.string().email('Invalid email').required('Email is required') });

  const handleSubmit = useCallback(async (values: { email: string }) => {
    setLoading(true);
    setError('');
    setCamLoginUrl('');
    try {
      await authForgotPassword(values.email);
      setSentEmail(values.email);
      setSent(true);
    } catch (err) {
      if (err instanceof AiSyncMigrationRequiredError) {
        setError(err.message);
        setCamLoginUrl(err.camLoginUrl);
      } else {
        setError(err instanceof Error ? err.message : 'Request failed');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  if (sent) {
    return (
      <AuthLayout>
        <Title>Check your email</Title>
        <SuccessBox>
          If an account exists for {sentEmail}, we sent a reset code. Check your inbox.
        </SuccessBox>
        <ActionArea>
          <SubmitButton onClick={() => navigate('/auth/reset-password', { state: { email: sentEmail } })}>
            Enter reset code
          </SubmitButton>
        </ActionArea>
        <BackLinkArea>
          <LinkText>
            <Link to="/auth/login">Back to login</Link>
          </LinkText>
        </BackLinkArea>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Title>Forgot password?</Title>
      <Subtitle>Enter your email and we&apos;ll send you a reset code.</Subtitle>
      <Formik initialValues={{ email: '' }} validationSchema={schema} onSubmit={handleSubmit}>
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit: formSubmit }) => (
          <Form onSubmit={formSubmit}>
            {error && camLoginUrl ? (
              <ErrorBox>
                {error}{' '}
                <InheritLink href={camLoginUrl}>
                  Go to Aibuild CAM login &rarr;
                </InheritLink>
              </ErrorBox>
            ) : error ? (
              <ErrorBox>{error}</ErrorBox>
            ) : null}
            <Input
              name="email" type="email" placeholder="Email address"
              value={values.email} onChange={handleChange} onBlur={handleBlur}
              $error={!!(touched.email && errors.email)}
            />
            {touched.email && errors.email && <ErrorText>{errors.email}</ErrorText>}
            <SubmitButton type="submit" disabled={loading} $loading={loading}>
              {loading ? 'Sending...' : 'Send reset code'}
            </SubmitButton>
            <LinkText><Link to="/auth/login">Back to login</Link></LinkText>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
};

export default ForgotPassword;
